import { STYLE_TAG_ID } from '../Layout/tokens';

// Copying app-wide CSS appends real <style>/<link> nodes to the target document,
// so repeated calls would duplicate CSS in the popout. WeakSet gives us
// once-per-Document behavior without keeping closed popout documents alive.
const copiedDocuments = new WeakSet<Document>();

/**
 * Copies app-wide static CSS into a popout document once.
 *
 * This covers styles that are not owned by styled-components, such as AG Grid,
 * font/icon CSS, and other library stylesheets. Runtime styled-components sheets
 * are intentionally skipped: popout React subtrees generate those rules locally
 * through PopoutStyleProvider, and copied parent styled-components rules can
 * create same-specificity cascade ties with popout-local rules.
 */
export function copyStaticStyles(targetDocument: Document, sourceDocument: Document = document) {
  if (copiedDocuments.has(targetDocument)) {
    return;
  }

  copyStaticStyleSheets(targetDocument, sourceDocument.styleSheets);
  copiedDocuments.add(targetDocument);
}

function copyStaticStyleSheets(targetDocument: Document, styleSheets: Iterable<CSSStyleSheet>) {
  for (const styleSheet of styleSheets) {
    if (!shouldCopyStaticStyleSheet(styleSheet)) {
      continue;
    }

    copyStyleSheet(targetDocument, styleSheet);
  }
}

export function shouldCopyStaticStyleSheet(styleSheet: CSSStyleSheet) {
  const ownerElement = getOwnerElement(styleSheet);
  if (ownerElement == null) {
    return true;
  }

  if (ownerElement.parentElement?.id === STYLE_TAG_ID) {
    // Ava's root StyleSheetManager writes styled-components rules under this wrapper.
    return false;
  }

  // Cypress and some styled-components paths use the default data-styled marker.
  return !ownerElement.hasAttribute('data-styled');
}

function copyStyleSheet(targetDocument: Document, styleSheet: CSSStyleSheet) {
  // There are different types of styleSheets, so copy them over using different methods depending on their nature
  if (styleSheet.href) {
    copyLinkStyleSheet(targetDocument, styleSheet);
    return;
  }

  const ownerElement = getOwnerElement(styleSheet);
  if (ownerElement instanceof HTMLStyleElement && ownerElement.textContent) {
    // Prefer the existing <style> text when it is available; no need to rebuild it from cssRules.
    copyInlineStyle(targetDocument, ownerElement.textContent);
    return;
  }

  let cssText = '';
  try {
    cssText = Array.from(styleSheet.cssRules)
      .map(rule => rule.cssText)
      .join('\n');
  } catch (err) {
    // Cross-origin stylesheets can deny cssRules access. Skipping one sheet is
    // better than failing the popout open path.
    return;
  }

  if (cssText) {
    copyInlineStyle(targetDocument, cssText);
  }
}

function copyLinkStyleSheet(targetDocument: Document, styleSheet: CSSStyleSheet) {
  const link = targetDocument.createElement('link');
  link.href = styleSheet.href ?? '';
  link.rel = 'stylesheet';

  const ownerElement = getOwnerElement(styleSheet);
  if (ownerElement instanceof HTMLLinkElement) {
    // Preserve loading/security attributes from the original link stylesheet.
    link.media = ownerElement.media;
    link.crossOrigin = ownerElement.crossOrigin;
    link.integrity = ownerElement.integrity;
    link.referrerPolicy = ownerElement.referrerPolicy;
  }

  targetDocument.head.appendChild(link);
}

function copyInlineStyle(targetDocument: Document, cssText: string) {
  const style = targetDocument.createElement('style');
  style.textContent = cssText;
  targetDocument.head.appendChild(style);
}

function getOwnerElement(styleSheet: CSSStyleSheet) {
  return styleSheet.ownerNode instanceof Element ? styleSheet.ownerNode : undefined;
}
